<script lang="ts">
	import { goto } from '$app/navigation';
	import { page } from '$app/state';
	import type { Market } from '$lib/types/market';
	import type { User } from '$lib/types/user';
	import ModernHeader from './ModernHeader.svelte';

	interface Props {
		errorCode: string;
		theme: string;
		imageSrc: string;
		imageAlt: string;
		caption: string;
		showHomeButton?: boolean;
		showBackButton?: boolean;
		market?: Market;
		user?: User;
		loggedIn?: boolean;
		placeholder?: boolean;
	}

	let {
		errorCode,
		theme,
		imageSrc,
		imageAlt,
		caption,
		showHomeButton = true,
		showBackButton = true,
		market,
		user,
		loggedIn = false,
		placeholder = true,
	}: Props = $props();

	function goHome() {
		window.location.href = '/market';
	}

	function goBack() {
		if (document.referrer && document.referrer !== window.location.href) {
			window.history.back();
		} else {
			goto(page.url.origin + '/market');
		}
	}

	function handleImageError(event: Event) {
		const target = event.target as HTMLImageElement;
		target.style.display = 'none';
		const fallback = target.nextElementSibling as HTMLElement;
		if (fallback) {
			fallback.style.display = 'block';
		}
	}
</script>

<svelte:head>
	<title>Error {errorCode} | LocallyGrown</title>
</svelte:head>

<div class="error-page page-container" data-theme={theme}>
	<div class="page-header">
		<ModernHeader {market} {user} {loggedIn} showHelpButton={false} />
	</div>

	<div class="error-container">
		<div class="error-content">
			<!-- Error Code and Theme Header -->
			<div class="error-header">
				<h1 class="error-code" aria-label="Error code {errorCode}">{errorCode}</h1>
				<h2 class="error-theme">{theme}</h2>
			</div>

			<!-- Illustration -->
			<div class="error-illustration">
				<div class="image-container">
					{#if placeholder}
						<p class="fallback-text">Hand-drawn illustration coming soon!</p>
					{/if}
					<img
						loading="lazy"
						src={imageSrc}
						alt={imageAlt}
						class="error-image"
						onerror={handleImageError}
					/>
					<!-- Fallback placeholder when image fails to load -->
					<div class="image-fallback" style="display: none;">
						<div class="fallback-icon">🏪</div>
						<p class="fallback-text">Illustration coming soon</p>
					</div>
				</div>
			</div>

			<!-- Caption -->
			<p class="error-caption">{caption}</p>

			<!-- Action Buttons -->
			<div class="error-actions">
				{#if showBackButton}
					<button
						class="btn-secondary"
						onclick={goBack}
						type="button"
						aria-label="Go back to previous page"
					>
						← Back
					</button>
				{/if}
				{#if showHomeButton}
					<button
						class="btn-primary"
						onclick={goHome}
						type="button"
						aria-label="Return to market homepage"
					>
						🏠 Back to Market
					</button>
				{/if}
			</div>

			<!-- Optional Footer -->
			<footer class="error-footer">
				<p class="help-text">
					Need help? <a href="mailto:support@locallygrown.net" class="support-link">
						Contact Support
					</a>
				</p>
			</footer>
		</div>

		<!-- Decorative Background Pattern -->
		<div class="background-pattern" aria-hidden="true"></div>
	</div>
</div>

<style>
	.error-page {
		min-height: 100vh;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: var(--lg-space-4);
		background: var(--lg-warm-50, #fefdfb);
		font-family: var(--lg-font-sans, 'Inter', sans-serif);
		position: relative;
		overflow: hidden;
	}

	.error-container {
		position: relative;
		width: 100%;
		max-width: 800px;
		z-index: 1;
	}

	.error-content {
		text-align: center;
		background: var(--lg-bg-primary);
		border-radius: var(--lg-radius-xl, 1rem);
		box-shadow: var(--lg-shadow-lg, 0 10px 25px -5px rgba(0, 0, 0, 0.1));
		padding: var(--lg-space-12, 3rem) var(--lg-space-8, 2rem);
		border: 3px solid var(--lg-warm-200, #f5f5f0);
		position: relative;
	}

	/* Error Header */
	.error-header {
		margin-bottom: var(--lg-space-8, 2rem);
	}

	.error-code {
		font-size: clamp(4rem, 8vw, 8rem);
		font-weight: 800;
		color: var(--lg-primary, #8b4513);
		margin: 0;
		line-height: 1;
		font-family: 'Courier New', monospace;
		text-shadow: 2px 2px 4px rgba(139, 69, 19, 0.2);
		letter-spacing: -0.02em;
	}

	.error-theme {
		font-size: clamp(1.5rem, 4vw, 2rem);
		font-weight: 600;
		color: var(--lg-warm-700, #78716c);
		margin: var(--lg-space-2, 0.5rem) 0 0 0;
		font-family: 'Georgia', serif;
		font-style: italic;
	}

	/* Illustration */
	.error-illustration {
		margin: var(--lg-space-8, 2rem) 0;
	}

	.image-container {
		position: relative;
		display: inline-block;
		max-width: 400px;
		width: 100%;
	}

	.error-image {
		width: 100%;
		height: auto;
		border-radius: var(--lg-radius-lg, 0.75rem);
		box-shadow: var(--lg-shadow-md, 0 4px 12px rgba(0, 0, 0, 0.1));
		border: 2px solid var(--lg-warm-200, #f5f5f0);
	}

	.image-fallback {
		background: var(--lg-warm-100, #f5f5f4);
		border: 2px dashed var(--lg-warm-300, #e7e5e4);
		border-radius: var(--lg-radius-lg, 0.75rem);
		padding: var(--lg-space-8, 2rem);
		text-align: center;
	}

	.fallback-icon {
		font-size: 4rem;
		margin-bottom: var(--lg-space-4, 1rem);
		opacity: 0.7;
	}

	.fallback-text {
		color: var(--lg-warm-600, #78716c);
		font-size: var(--lg-text-sm, 0.875rem);
		margin: 0;
		font-style: italic;
	}

	/* Caption */
	.error-caption {
		font-size: clamp(1.125rem, 3vw, 1.25rem);
		color: var(--lg-warm-700, #78716c);
		line-height: var(--lg-leading-relaxed, 1.625);
		margin: var(--lg-space-6, 1.5rem) 0;
		font-weight: 500;
		max-width: 600px;
		margin-left: auto;
		margin-right: auto;
	}

	/* Action Buttons */
	.error-actions {
		display: flex;
		gap: var(--lg-space-4, 1rem);
		justify-content: center;
		flex-wrap: wrap;
		margin: var(--lg-space-8, 2rem) 0;
	}

	.btn-primary,
	.btn-secondary {
		padding: var(--lg-space-3, 0.75rem) var(--lg-space-6, 1.5rem);
		border-radius: var(--lg-radius-md, 0.5rem);
		font-size: var(--lg-text-base, 1rem);
		font-weight: 600;
		cursor: pointer;
		transition: all var(--lg-transition-fast, 0.15s) ease;
		border: 2px solid transparent;
		text-decoration: none;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: var(--lg-space-2, 0.5rem);
		min-width: 140px;
		font-family: inherit;
	}

	.btn-primary {
		background: var(--lg-primary, #8b4513);
		color: var(--lg-text-white);
		border-color: var(--lg-primary, #8b4513);
		box-shadow: var(--lg-shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.1));
	}

	.btn-primary:hover,
	.btn-primary:focus,
	.btn-primary:active {
		background: var(--lg-primary-hover, #7a3d11);
		border-color: var(--lg-primary-hover, #7a3d11);
		transform: translateY(-2px);
		box-shadow: var(--lg-shadow-md, 0 4px 12px rgba(139, 69, 19, 0.3));
	}

	.btn-secondary {
		background: transparent;
		color: var(--lg-warm-700, #78716c);
		border-color: var(--lg-warm-300, #e7e5e4);
	}

	.btn-secondary:hover,
	.btn-secondary:focus,
	.btn-secondary:active {
		background: var(--lg-warm-100, #f5f5f4);
		border-color: var(--lg-warm-400, #a8a29e);
		transform: translateY(-2px);
	}

	/* Footer */
	.error-footer {
		margin-top: var(--lg-space-8, 2rem);
		padding-top: var(--lg-space-6, 1.5rem);
		border-top: 1px solid var(--lg-warm-200, #f5f5f0);
	}

	.help-text {
		font-size: var(--lg-text-sm, 0.875rem);
		color: var(--lg-warm-600, #78716c);
		margin: 0;
	}

	.support-link {
		color: var(--lg-primary, #8b4513);
		text-decoration: underline;
		font-weight: 500;
	}

	.support-link:hover,
	.support-link:focus,
	.support-link:active {
		color: var(--lg-primary-hover, #7a3d11);
		text-decoration: none;
	}

	/* Background Pattern */
	.background-pattern {
		position: absolute;
		inset: 0;
		background-image:
			radial-gradient(circle at 25% 25%, var(--lg-warm-100, #f5f5f4) 2px, transparent 2px),
			radial-gradient(circle at 75% 75%, var(--lg-warm-100, #f5f5f4) 1px, transparent 1px);
		background-size:
			40px 40px,
			20px 20px;
		opacity: 0.5;
		z-index: 0;
		pointer-events: none;
	}

	/* Responsive Design */
	@media (max-width: 640px) {
		.error-page {
			padding: var(--lg-space-4, 1rem);
		}

		.error-content {
			padding: var(--lg-space-8, 2rem) var(--lg-space-4, 1rem);
		}

		.error-actions {
			flex-direction: column;
			align-items: center;
		}

		.btn-primary,
		.btn-secondary {
			width: 100%;
			max-width: 280px;
		}
	}

	/* Focus Styles for Accessibility */
	button:focus-visible {
		outline: 3px solid var(--lg-primary, #8b4513);
		outline-offset: 2px;
	}

	/* High Contrast Mode Support */
	@media (prefers-contrast: high) {
		.error-content {
			border-width: 4px;
			border-color: currentColor;
		}

		.error-code {
			text-shadow: none;
		}
	}

	/* Animation for Error Code */
	@keyframes fadeInUp {
		from {
			opacity: 0;
			transform: translateY(20px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	.error-code {
		animation: fadeInUp 0.8s ease-out;
	}

	.error-theme {
		animation: fadeInUp 0.8s ease-out 0.2s both;
	}

	.error-illustration {
		animation: fadeInUp 0.8s ease-out 0.4s both;
	}

	.error-caption {
		animation: fadeInUp 0.8s ease-out 0.6s both;
	}

	.error-actions {
		animation: fadeInUp 0.8s ease-out 0.8s both;
	}

	/* Reduce motion for accessibility */
	@media (prefers-reduced-motion: reduce) {
		.error-code,
		.error-theme,
		.error-illustration,
		.error-caption,
		.error-actions {
			animation: none;
		}

		.btn-primary:hover,
		.btn-secondary:hover,
		.btn-secondary:focus,
		.btn-secondary:active {
			transform: none;
		}
	}
</style>
